/**
 * Small pieces shared by the member screens, built to the admin console's
 * wardrobe-grid and detail-list conventions so both apps read alike.
 */

import { useState, type ReactNode } from 'react';
import type { MemberGarmentSummary } from '@closet-theory/api';
import { Badge } from '@ui/components/base/badges/badges';
import type { BadgeColors } from '@ui/components/base/badges/badge-types';
import { cx } from '@ui/utils/cx';
import { GarmentImage } from './GarmentImage';
import { garmentName, garmentStatus, humanize, initials } from './member-format';
import type { ReviewGarment } from './review-service';

export const GARMENT_GRID_CLASS = 'grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-5';
export const GARMENT_TILE_CLASS =
  'flex w-full min-w-0 cursor-pointer flex-col gap-2 rounded-xl border border-secondary bg-primary p-3 text-left outline-brand hover:border-brand focus-visible:outline-2';

export function toReviewGarment(garment: MemberGarmentSummary): ReviewGarment {
  return {
    garmentId: garment.id,
    title: garmentName(garment),
    category: garment.category ?? '',
    tags: [],
    imageUrl: garment.image_url ?? null,
  };
}

export function PlainBadge({ label, color }: { label: string; color: BadgeColors }) {
  return (
    <Badge type="pill-color" size="sm" color={color}>
      {label}
    </Badge>
  );
}

export function MemberAvatar({
  name,
  src,
  size = 'md',
}: {
  name: string;
  src?: string | null | undefined;
  size?: 'sm' | 'md' | 'lg';
}) {
  const [failed, setFailed] = useState(false);
  const box =
    size === 'lg' ? 'size-16 text-lg' : size === 'sm' ? 'size-9 text-xs' : 'size-12 text-sm';
  if (src && !failed) {
    return (
      <img
        src={src}
        alt={`${name}'s portrait`}
        className={cx(box, 'shrink-0 rounded-full bg-secondary object-cover')}
        onError={() => setFailed(true)}
      />
    );
  }
  return (
    <span
      aria-hidden="true"
      className={cx(
        box,
        'flex shrink-0 items-center justify-center rounded-full bg-brand-secondary font-semibold text-brand-secondary',
      )}
    >
      {initials(name)}
    </span>
  );
}

/**
 * Label/value pairs, matching the admin console's detail list. `dense` keeps
 * two columns whatever the screen, for a narrow pane beside other content.
 */
export function DetailList({
  items,
  dense,
}: {
  items: Array<[ReactNode, ReactNode]>;
  dense?: boolean | undefined;
}) {
  return (
    <dl
      className={cx(
        'grid gap-x-6 gap-y-4',
        dense ? 'grid-cols-2' : 'grid-cols-1 sm:grid-cols-2 xl:grid-cols-3',
      )}
    >
      {items.map(([label, value], index) => (
        <div key={index} className="flex min-w-0 flex-col gap-0.5">
          <dt className="text-xs text-tertiary">{label}</dt>
          <dd className="m-0 min-w-0 text-sm font-medium text-secondary">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

/** A bordered card, the same surface as the console's stat tiles and sections. */
export function Card({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cx(
        'flex flex-col gap-4 rounded-xl border border-secondary bg-primary p-5',
        className,
      )}
    >
      {children}
    </div>
  );
}

export function GarmentThumbs({
  garments,
  label,
  size = 'sm',
  max = 6,
}: {
  garments: ReviewGarment[];
  label: string;
  size?: 'xs' | 'sm';
  max?: number;
}) {
  const shown = garments.slice(0, max);
  const hidden = garments.length - shown.length;
  return (
    <ul aria-label={label} className="flex flex-wrap items-center gap-1">
      {shown.map((garment) => (
        <li
          key={garment.garmentId}
          aria-label={`${garment.title || garment.category || 'Untitled garment'} garment`}
        >
          <GarmentImage garment={garment} size={size} />
        </li>
      ))}
      {hidden > 0 ? <li className="px-1 text-xs text-tertiary">+{hidden} more</li> : null}
    </ul>
  );
}

/** One wardrobe tile: the whole tile is the control, as on the admin wardrobe grid. */
export function GarmentTile({
  garment,
  onOpen,
  selected,
  disabled,
  pressable = false,
}: {
  garment: MemberGarmentSummary;
  onOpen: () => void;
  selected?: boolean;
  disabled?: boolean;
  /** A toggle in a picker rather than a link to a detail dialog. */
  pressable?: boolean;
}) {
  const name = garmentName(garment);
  const note = garmentStatus(garment.status);
  return (
    <li className="min-w-0">
      <button
        type="button"
        onClick={onOpen}
        disabled={disabled}
        {...(pressable
          ? { 'aria-pressed': Boolean(selected) }
          : { 'aria-haspopup': 'dialog' as const })}
        aria-label={pressable ? `${selected ? 'Remove' : 'Add'} ${name}` : name}
        className={cx(
          GARMENT_TILE_CLASS,
          selected && 'border-brand ring-2 ring-brand',
          disabled && 'cursor-not-allowed opacity-50 hover:border-secondary',
        )}
      >
        <span className="relative block">
          <GarmentImage garment={toReviewGarment(garment)} size="tile" />
          {selected ? (
            <span className="absolute top-2 right-2 rounded-full bg-brand-solid px-2 py-0.5 text-xs font-semibold text-white">
              Selected
            </span>
          ) : null}
        </span>
        <span className="truncate text-sm font-medium text-primary" title={name}>
          {name}
        </span>
        <span className="truncate text-xs text-tertiary">
          {humanize(garment.category) || 'Category not recorded'}
        </span>
        {note ? <span className="text-xs text-warning-primary">{note}</span> : null}
      </button>
    </li>
  );
}

export function categoriesOf(garments: readonly MemberGarmentSummary[]): string[] {
  return [...new Set(garments.map((garment) => garment.category ?? '').filter(Boolean))].sort();
}
